import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertCircle,
  CheckCircle2,
  Loader2,
  Package,
  DollarSign,
  Hash,
  Layers,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { DISCOUNT_TIERS } from '../../../utils/discountTiers';

const CATEGORIES = [
  "Baby & Kids", "Bakery & Bread", "Beverages", "Breakfast & Cereal",
  "Dairy & Eggs", "Deli & Prepared Foods", "Frozen Foods",
  "Fruits", "Health & Nutrition", "Household Essentials",
  "Meat & Poultry", "Oils, Sauces & Spices", "Pantry Staples",
  "Pasta, Rice & Grains", "Personal Care", "Pet Supplies",
  "Seafood", "Snacks & Candy", "Vegetables", "Miscellaneous"
];

const DISCOUNT_LEVELS = DISCOUNT_TIERS.map((t) => ({
  value: t.level,
  label: `Tier ${t.level} - ${t.consumer}% off`,
  name: t.name,
}));

/**
 * BatchConfirmationGrid - Shared component for reviewing and editing batch barcode scans
 * 
 * Used by:
 * - ShelfScanUpload (Phase 1A)
 * - ContinuousScanMode (Phase 2A - future)
 * - CatalogQuickSelect (Phase 1B)
 */
export default function BatchConfirmationGrid({
  items,
  onSubmit,
  onCancel,
  loading = false,
  title = "Review Products",
  submitLabel = "Post All Selected"
}) {
  const { t } = useLanguage();
  
  // Track editable state for each item
  const [editableItems, setEditableItems] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkQuantity, setBulkQuantity] = useState('');
  const [bulkDiscount, setBulkDiscount] = useState('');
  const [showBulkActions, setShowBulkActions] = useState(false);

  // Initialize editable items from props
  useEffect(() => {
    const initialized = items.map((item, index) => ({
      id: `item-${index}`,
      raw_code: item.raw_code || '',
      barcode_type: item.barcode_type || item.parsed?.format || 'Unknown',
      name: item.parsed?.product_name || item.name || '',
      category: item.parsed?.category || item.category || '',
      regular_price: item.regular_price || '',
      quantity: item.quantity || 10,
      discount_level: item.discount_level || 2,
      confidence: item.confidence || item.parsed?.confidence || 0,
      source: item.source || item.parsed?.source || 'barcode',
      ready_to_post: item.ready_to_post || false,
      needs_input: item.needs_input || !item.parsed?.product_name,
      barcode_data: item.parsed || item.barcode_data || { raw_code: item.raw_code }
    }));
    
    setEditableItems(initialized);
    
    // Auto-select items that are ready
    const readyIds = new Set(initialized.filter(i => i.ready_to_post && !i.needs_input).map(i => i.id));
    setSelectedIds(readyIds);
  }, [items]);

  // Calculate stats
  const totalItems = editableItems.length;
  const selectedCount = selectedIds.size;
  const readyCount = editableItems.filter(i => isItemValid(i)).length;
  const needsAttentionCount = editableItems.filter(i => !isItemValid(i)).length;

  // Validation helper
  function isItemValid(item) {
    return (
      item.name?.trim() &&
      item.category &&
      item.regular_price > 0 &&
      item.quantity >= 5 &&
      [1, 2, 3].includes(item.discount_level)
    );
  }

  // Get confidence indicator
  function getConfidenceIndicator(confidence, needsInput) {
    if (needsInput) return { color: 'text-red-500', bg: 'bg-red-50', icon: AlertCircle, label: t('batch_grid.needs_input', 'Needs Input') };
    if (confidence >= 0.8) return { color: 'text-green-500', bg: 'bg-green-50', icon: CheckCircle2, label: t('batch_grid.ready_status', 'Ready') };
    if (confidence >= 0.5) return { color: 'text-yellow-500', bg: 'bg-yellow-50', icon: AlertCircle, label: t('batch_grid.review', 'Review') };
    return { color: 'text-red-500', bg: 'bg-red-50', icon: AlertCircle, label: t('batch_grid.needs_input', 'Needs Input') };
  }

  // Update single item
  const updateItem = useCallback((id, field, value) => {
    setEditableItems(prev => prev.map(item => {
      if (item.id !== id) return item;
      const updated = { ...item, [field]: value };
      // Re-evaluate ready status
      updated.needs_input = !isItemValid(updated);
      updated.ready_to_post = isItemValid(updated);
      return updated;
    }));
  }, []);

  // Toggle selection
  const toggleSelection = useCallback((id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  // Select all valid
  const selectAllValid = useCallback(() => {
    const validIds = editableItems.filter(i => isItemValid(i)).map(i => i.id);
    setSelectedIds(new Set(validIds));
  }, [editableItems]);

  // Deselect all
  const deselectAll = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  // Apply bulk quantity
  const applyBulkQuantity = useCallback(() => {
    const qty = parseInt(bulkQuantity, 10);
    if (qty >= 5) {
      setEditableItems(prev => prev.map(item => {
        if (selectedIds.has(item.id)) {
          const updated = { ...item, quantity: qty };
          updated.needs_input = !isItemValid(updated);
          updated.ready_to_post = isItemValid(updated);
          return updated;
        }
        return item;
      }));
      setBulkQuantity('');
    }
  }, [bulkQuantity, selectedIds]);

  // Apply bulk discount
  const applyBulkDiscount = useCallback(() => {
    const level = parseInt(bulkDiscount, 10);
    if ([1, 2, 3].includes(level)) {
      setEditableItems(prev => prev.map(item => {
        if (selectedIds.has(item.id)) {
          const updated = { ...item, discount_level: level };
          updated.needs_input = !isItemValid(updated);
          updated.ready_to_post = isItemValid(updated);
          return updated;
        }
        return item;
      }));
      setBulkDiscount('');
    }
  }, [bulkDiscount, selectedIds]);

  // Handle submit
  const handleSubmit = useCallback(() => {
    const selectedItems = editableItems.filter(item => selectedIds.has(item.id) && isItemValid(item));
    if (selectedItems.length === 0) return;
    
    // Prepare items for API
    const apiItems = selectedItems.map(item => ({
      barcode_data: {
        ...item.barcode_data,
        raw_code: item.raw_code,
        format: item.barcode_type,
        confidence: item.confidence,
        source: item.source
      },
      name: item.name,
      category: item.category,
      regular_price: parseFloat(item.regular_price),
      quantity: parseInt(item.quantity, 10),
      discount_level: parseInt(item.discount_level, 10)
    }));
    
    onSubmit(apiItems);
  }, [editableItems, selectedIds, onSubmit]);

  return (
    <Card className="w-full">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <Package className="w-5 h-5 text-blue-600" />
              {title}
            </CardTitle>
            <CardDescription className="mt-1">
              {totalItems} {t('batch_grid.products_detected', 'products detected')} • {readyCount} {t('batch_grid.ready', 'ready')} • {needsAttentionCount} {t('batch_grid.need_attention', 'need attention')}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="bg-green-50 text-green-700">
              <CheckCircle2 className="w-3 h-3 mr-1" />
              {selectedCount} {t('batch_grid.selected', 'selected')}
            </Badge>
            {needsAttentionCount > 0 && (
              <Badge variant="outline" className="bg-red-50 text-red-700">
                <AlertCircle className="w-3 h-3 mr-1" />
                {needsAttentionCount} {t('batch_grid.need_input', 'need input')}
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="pt-0">
        {/* Bulk Actions Bar */}
        <div className="mb-4 p-3 bg-gray-50 rounded-lg">
          <button
            onClick={() => setShowBulkActions(!showBulkActions)}
            className="flex items-center gap-2 text-sm font-medium text-gray-700 w-full"
            data-testid="bulk-actions-toggle"
          >
            <Layers className="w-4 h-4" />
            {t('batch_grid.bulk_actions', 'Bulk Actions')}
            {showBulkActions ? <ChevronUp className="w-4 h-4 ml-auto" /> : <ChevronDown className="w-4 h-4 ml-auto" />}
          </button>
          
          {showBulkActions && (
            <div className="mt-3 flex flex-wrap gap-4 items-end">
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={selectAllValid} data-testid="select-all-btn">
                  {t('batch_grid.select_all_valid', 'Select All Valid')} ({readyCount})
                </Button>
                <Button variant="ghost" size="sm" onClick={deselectAll}>
                  {t('batch_grid.deselect_all', 'Deselect All')}
                </Button>
              </div>
              
              <div className="flex items-center gap-2">
                <Label className="text-xs text-gray-500">{t('batch_grid.set_qty', 'Set Qty:')}</Label>
                <Input
                  type="number"
                  placeholder="10"
                  value={bulkQuantity}
                  onChange={(e) => setBulkQuantity(e.target.value)}
                  className="w-20 h-8"
                  min={5}
                  data-testid="bulk-quantity-input"
                />
                <Button variant="secondary" size="sm" onClick={applyBulkQuantity} disabled={!bulkQuantity}>
                  {t('batch_grid.apply', 'Apply')}
                </Button>
              </div>
              
              <div className="flex items-center gap-2">
                <Label className="text-xs text-gray-500">{t('batch_grid.set_tier', 'Set Tier:')}</Label>
                <Select value={bulkDiscount} onValueChange={setBulkDiscount}>
                  <SelectTrigger className="w-32 h-8" data-testid="bulk-discount-select">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {DISCOUNT_LEVELS.map(level => (
                      <SelectItem key={level.value} value={level.value.toString()}>
                        {level.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button variant="secondary" size="sm" onClick={applyBulkDiscount} disabled={!bulkDiscount}>
                  {t('batch_grid.apply', 'Apply')}
                </Button>
              </div>
            </div>
          )}
        </div>
        
        {/* Items Table */}
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="w-10">
                  <Checkbox
                    checked={selectedCount === totalItems && totalItems > 0}
                    onCheckedChange={(checked) => checked ? selectAllValid() : deselectAll()}
                    data-testid="select-all-checkbox"
                  />
                </TableHead>
                <TableHead className="w-24">{t('batch_grid.status', 'Status')}</TableHead>
                <TableHead>{t('batch_grid.product', 'Product')}</TableHead>
                <TableHead className="w-36">{t('batch_grid.category', 'Category')}</TableHead>
                <TableHead className="w-24">{t('batch_grid.price', 'Price')}</TableHead>
                <TableHead className="w-20">{t('batch_grid.qty', 'Qty')}</TableHead>
                <TableHead className="w-28">{t('batch_grid.discount', 'Discount')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {editableItems.map((item) => {
                const indicator = getConfidenceIndicator(item.confidence, item.needs_input);
                const isSelected = selectedIds.has(item.id);
                const isValid = isItemValid(item);
                
                return (
                  <TableRow
                    key={item.id}
                    className={`${isSelected ? 'bg-blue-50/50' : ''} ${!isValid ? 'bg-red-50/30' : ''}`}
                    data-testid={`batch-item-${item.raw_code}`}
                  >
                    <TableCell>
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => toggleSelection(item.id)}
                        disabled={!isValid}
                        data-testid={`checkbox-${item.raw_code}`}
                      />
                    </TableCell>
                    <TableCell>
                      <div className={`flex items-center gap-1 px-2 py-1 rounded text-xs ${indicator.bg}`}>
                        <indicator.icon className={`w-3 h-3 ${indicator.color}`} />
                        <span className={indicator.color}>{indicator.label}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <Input
                          value={item.name}
                          onChange={(e) => updateItem(item.id, 'name', e.target.value)}
                          placeholder={t('batch_grid.product_name_placeholder', 'Product name *')}
                          className={`h-8 ${!item.name?.trim() ? 'border-red-300' : ''}`}
                          data-testid={`name-input-${item.raw_code}`}
                        />
                        <div className="flex items-center gap-2">
                          <code className="text-[10px] font-mono bg-gray-100 px-1.5 py-0.5 rounded">
                            {item.raw_code}
                          </code>
                          <Badge variant="outline" className="text-[9px]">
                            {item.barcode_type}
                          </Badge>
                          {item.source === 'store_registry' && (
                            <Badge className="text-[9px] bg-blue-100 text-blue-700">{t('batch_grid.registry', 'Registry')}</Badge>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Select
                        value={item.category}
                        onValueChange={(v) => updateItem(item.id, 'category', v)}
                      >
                        <SelectTrigger className={`h-8 text-xs ${!item.category ? 'border-red-300' : ''}`} data-testid={`category-select-${item.raw_code}`}>
                          <SelectValue placeholder={t('batch_grid.select_category', 'Select *')} />
                        </SelectTrigger>
                        <SelectContent>
                          {CATEGORIES.map(cat => (
                            <SelectItem key={cat} value={cat} className="text-xs">{cat}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <div className="relative">
                        <DollarSign className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400" />
                        <Input
                          type="number"
                          step="0.01"
                          min="0.01"
                          value={item.regular_price}
                          onChange={(e) => updateItem(item.id, 'regular_price', parseFloat(e.target.value) || '')}
                          placeholder="0.00"
                          className={`h-8 pl-6 ${!item.regular_price || item.regular_price <= 0 ? 'border-red-300' : ''}`}
                          data-testid={`price-input-${item.raw_code}`}
                        />
                      </div>
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        min={5}
                        value={item.quantity}
                        onChange={(e) => updateItem(item.id, 'quantity', parseInt(e.target.value, 10) || 5)}
                        className={`h-8 ${item.quantity < 5 ? 'border-red-300' : ''}`}
                        data-testid={`quantity-input-${item.raw_code}`}
                      />
                    </TableCell>
                    <TableCell>
                      <Select
                        value={item.discount_level.toString()}
                        onValueChange={(v) => updateItem(item.id, 'discount_level', parseInt(v, 10))}
                      >
                        <SelectTrigger className="h-8 text-xs" data-testid={`discount-select-${item.raw_code}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {DISCOUNT_LEVELS.map(level => (
                            <SelectItem key={level.value} value={level.value.toString()} className="text-xs">
                              {level.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
        
        {/* Footer Actions */}
        <div className="flex items-center justify-between mt-4 pt-4 border-t">
          <div className="text-sm text-gray-500">
            <Checkbox
              checked={selectedCount === readyCount && readyCount > 0}
              onCheckedChange={(checked) => checked ? selectAllValid() : deselectAll()}
              className="mr-2"
            />
            {t('batch_grid.select_all_valid_count', 'Select All Valid')} ({readyCount}/{totalItems})
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={onCancel} disabled={loading}>
              {t('batch_grid.cancel', 'Cancel')}
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={loading || selectedCount === 0}
              className="min-w-[180px]"
              data-testid="batch-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('scanner_ui.processing', 'Processing...')}
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  {submitLabel} ({selectedCount})
                </>
              )}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
